// i18n: locale bundles are imported at build time (esbuild json loader). t() reads
// a dot-path key like 'common.actions.save' or 'auth.login.title' and interpolates
// {placeholder} params. New languages register additional bundles here.
//
// Convention: shared chrome lives in `common`; each page may have its own namespace
// (e.g. `students`, `settings`). Add a new namespace by importing its en + ro JSON
// and listing it in the bundles map below — keep en and ro structurally identical.
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enReporting from './locales/en/reporting.json';
import enStudents from './locales/en/students.json';
import enTeachers from './locales/en/teachers.json';
import enClasses from './locales/en/classes.json';
import enFamilies from './locales/en/families.json';
import enAccounting from './locales/en/accounting.json';
import enMedia from './locales/en/media.json';
import enEvents from './locales/en/events.json';
import enMailing from './locales/en/mailing.json';
import enSettings from './locales/en/settings.json';
import enPortal from './locales/en/portal.json';
import enOnboarding from './locales/en/onboarding.json';
import enSetup from './locales/en/setup.json';
import enTeacher from './locales/en/teacher.json';
import enUi from './locales/en/ui.json';
import enIrishDance from './locales/en/irish-dance.json';
import roCommon from './locales/ro/common.json';
import roAuth from './locales/ro/auth.json';
import roDashboard from './locales/ro/dashboard.json';
import roReporting from './locales/ro/reporting.json';
import roStudents from './locales/ro/students.json';
import roTeachers from './locales/ro/teachers.json';
import roClasses from './locales/ro/classes.json';
import roFamilies from './locales/ro/families.json';
import roAccounting from './locales/ro/accounting.json';
import roMedia from './locales/ro/media.json';
import roEvents from './locales/ro/events.json';
import roMailing from './locales/ro/mailing.json';
import roSettings from './locales/ro/settings.json';
import roPortal from './locales/ro/portal.json';
import roOnboarding from './locales/ro/onboarding.json';
import roSetup from './locales/ro/setup.json';
import roTeacher from './locales/ro/teacher.json';
import roUi from './locales/ro/ui.json';
import roIrishDance from './locales/ro/irish-dance.json';

type Bundle = Record<string, unknown>;

const bundles: Record<string, Record<string, Bundle>> = {
  en: {
    common: enCommon as Bundle, auth: enAuth as Bundle,
    dashboard: enDashboard as Bundle, reporting: enReporting as Bundle,
    students: enStudents as Bundle, teachers: enTeachers as Bundle,
    classes: enClasses as Bundle, families: enFamilies as Bundle,
    accounting: enAccounting as Bundle, media: enMedia as Bundle,
    events: enEvents as Bundle, mailing: enMailing as Bundle,
    settings: enSettings as Bundle, portal: enPortal as Bundle,
    onboarding: enOnboarding as Bundle, setup: enSetup as Bundle,
    teacher: enTeacher as Bundle, ui: enUi as Bundle,
    irishDance: enIrishDance as Bundle,
  },
  ro: {
    common: roCommon as Bundle, auth: roAuth as Bundle,
    dashboard: roDashboard as Bundle, reporting: roReporting as Bundle,
    students: roStudents as Bundle, teachers: roTeachers as Bundle,
    classes: roClasses as Bundle, families: roFamilies as Bundle,
    accounting: roAccounting as Bundle, media: roMedia as Bundle,
    events: roEvents as Bundle, mailing: roMailing as Bundle,
    settings: roSettings as Bundle, portal: roPortal as Bundle,
    onboarding: roOnboarding as Bundle, setup: roSetup as Bundle,
    teacher: roTeacher as Bundle, ui: roUi as Bundle,
    irishDance: roIrishDance as Bundle,
  },
};

/** Languages offered in the UI selectors. */
export const LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'ro', label: 'Română' },
];

/** localStorage key remembering the chosen locale across reloads / the login boundary. */
export const LOCALE_STORAGE_KEY = 'cadence.locale';

let current = 'en';

export function setLocale(locale: string): void {
  if (bundles[locale]) current = locale;
}

export function getLocale(): string {
  return current;
}

export function t(key: string, params?: Record<string, string | number>): string {
  const [ns, ...rest] = key.split('.');
  // Look up in the current locale, falling back to English so a missing translation
  // shows the English string rather than a raw key.
  let node: unknown = bundles[current]?.[ns];
  let fallback: unknown = bundles.en?.[ns];
  for (const part of rest) {
    node = node && typeof node === 'object' ? (node as Record<string, unknown>)[part] : undefined;
    fallback = fallback && typeof fallback === 'object' ? (fallback as Record<string, unknown>)[part] : undefined;
  }
  let str = typeof node === 'string' ? node : (typeof fallback === 'string' ? fallback : key);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return str;
}
